import { Fragment, useEffect, useState } from "react";

import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";

import { StatementInvoiceService } from '../StatementInvoiceService';
import { InvoiceService } from '../../hoa-don/InvoiceService';
import { OneBtsCatalogService } from '../../../service/invoice/OneBtsCatalogService';
import { getLawBasesText } from '../../../utils/statementHelper';
import { isRegisteredAssetInvoice } from '../../../utils/invoiceHelper';

import { useForm } from "../../../shared/hooks/Form";
import { ToastService } from "../../../AppToast";

import { FormatDisplay } from '../../../shared/format';
import { translateError } from '../../../shared/language';
import { useSign } from '../../../shared/component/Sign';
import moment from 'moment';
import { DialogSendEmail } from '../../hoa-don/component/DialogSendEmail';
import { ConfirmDialogService } from '../../../AppConfirmDialog';
import { useTranslation } from '../../../shared/language';

export const DialogStatement = ({
  title,
  display,
  setDisplay,
  selectedItem,
  registerInvoices = [],
  enableBts01ReasonCatalog = false,
  reloadLazyData,
  documentType,
}) => {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [isRequiredBuyerPersonContact, setIsRequiredBuyerPersonContact] =
    useState(false);
  const [emailDialogVisible, setEmailDialogVisible] = useState(false);

  const handleComplete = () => {
    load();
    // reloadLazyData();
  };

  const { renderSign, sign } = useSign({ onComplete: handleComplete });

  interface StatementData {
    id?: string;
    dateSign?: string;
    buyerDateSign?: string;
    documentDate?: Date | string;
    invoiceId?: string;
    invoiceSerial?: string;
    invoiceNumber?: string;
    invoiceDate?: string;
    reasonAfter?: string;
    documentType?: string;
    [key: string]: any; // Add this if there are more dynamic fields
  }

  const [id, setId] = useState<string | null>(null);
  const [data, setData] = useState<StatementData>({});

  useEffect(() => {
    if (display) {
      load();
    } else {
      reloadLazyData();
    }
  }, [display]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    try {
      const selectedInvoice = Array.isArray(selectedItem)
        ? selectedItem[0]
        : selectedItem;
      const data = await StatementInvoiceService.getByInvoice(
        selectedInvoice?.invoiceId || selectedInvoice?.id,
      );
      if (data && data.id) {
        const statementDocumentType = [
          InvoiceService.documentType_Adjust,
          InvoiceService.documentType_Substitute,
        ].includes(Number(data.documentType))
          ? Number(data.documentType)
          : Number(documentType);
        const shouldUseBts01ReasonCatalog =
          enableBts01ReasonCatalog &&
          [
            InvoiceService.documentType_Adjust,
            InvoiceService.documentType_Substitute,
          ].includes(statementDocumentType) &&
          isRegisteredAssetInvoice(selectedInvoice, registerInvoices);
        const reasonValue =
          data.reason ||
          (shouldUseBts01ReasonCatalog ? selectedInvoice?.reasons : null) ||
          selectedInvoice?.invoiceNote ||
          "";

        // Chỉ biên bản điều chỉnh hoặc thay thế BTS01 cần đổi mã lý do sang tên hiển thị từ danh mục.
        if (
          shouldUseBts01ReasonCatalog &&
          reasonValue &&
          data.documentType !== InvoiceService.documentType_Cancel
        ) {
          const response = await OneBtsCatalogService.getAdjustmentReasonCatalog(
            statementDocumentType,
          )
            .catch(() => []);
          const options = Array.isArray(response)
            ? response
            : Array.isArray(response?.options)
              ? response.options
              : Array.isArray(response?.items)
                ? response.items
                : [];
          const selectedReason = options.find(
            option => String(option.value) === String(reasonValue),
          );
          data.reason = selectedReason?.label || reasonValue;
        } else if (!data.reason) {
          data.reason = reasonValue;
        }
        if (data.id === '00000000-0000-0000-0000-000000000000') {
          const rawInvoiceDate = data.invoiceDate || selectedInvoice?.relatedInvoiceDate || selectedInvoice?.invoiceDate;
          data.documentDate = rawInvoiceDate ? new Date(rawInvoiceDate) : new Date();
        } else if (data.documentDate) {
          data.documentDate = new Date(data.documentDate);
        }

        if (!data.invoiceId) {
          data.invoiceId = selectedInvoice?.invoiceId;
        }

        if (!data.invoiceListNumber) {
          data.invoiceListNumber =
            selectedInvoice?.invoiceListNumber ||
            selectedInvoice?.relatedInvoiceListNumber;
        }
        if (!data.invoiceListDate) {
          data.invoiceListDate =
            selectedInvoice?.invoiceListDate || selectedInvoice?.relatedInvoiceListDate;
        }

        if (data.documentType !== InvoiceService.documentType_Cancel) {
          if (selectedInvoice?.relatedInvoiceSerial) {
            data.invoiceSerial =
              selectedInvoice.relatedInvoiceSerial.length === 6
                ? `${selectedInvoice.relatedTemplateCode}${selectedInvoice.relatedInvoiceSerial}`
                : selectedInvoice.relatedInvoiceSerial;
          }
          data.invoiceNumber = selectedInvoice?.relatedInvoiceNumber || data.invoiceNumber;
          data.invoiceDate = selectedInvoice?.relatedInvoiceDate || data.invoiceDate;
        }

        if (!data.reasonAfter) {
          data.reasonAfter = getLawBasesText(data.documentDate);
        }

        // data.documentType = documentType;
        setData(data);
        setValue(data);

        if (data.id === "00000000-0000-0000-0000-000000000000") {
          setId(null);
        } else {
          setId(data.id);
        }
        return data;
      }
      return null;
    } catch (error) {
      // handle error if needed
      return null;
    }
  };

  const getInvoiceId = () => {
    let invoiceId = selectedItem.invoiceId;
    if (
      documentType != InvoiceService.documentType_Cancel &&
      selectedItem.relatedInvoiceId
    ) {
      invoiceId = selectedItem.relatedInvoiceId;
    }
    return invoiceId;
  };

  const doSign = () => {
    // sign({
    //   "CommandName": "SignSS04",
    //   "Id": selectedItems['id']
    // });
  };

  const {
    render: renderForm,
    setValue,
    valid: formValid,
    getValue,
  } = useForm({
    fields: [
      { field: "id", type: "hidden" },
      { field: "documentType", type: "hidden" },
      { field: "invoiceId", type: "hidden" },
      { field: "invoiceSerial", type: "hidden" },
      { field: "taxAuthorityCode", type: "hidden" },
      { field: "totalAmount", type: "hidden" },
      { field: "invoiceNumber", type: "hidden" },
      { field: "invoiceDate", type: "hidden" },
      { field: "invoiceListNumber", type: "hidden" },
      { field: "invoiceListDate", type: "hidden" },
      {
        type: "custom",
        className: "md:col-12 text-center mb-0 mt-1",
        body: (
          <h3 className="mb-0 uppercase">{t('common.republicTitle')}</h3>
        ),
      },
      {
        type: "custom",
        className: "md:col-12 text-center mb-4",
        body: <b>{t('common.nationalMotto')}</b>,
      },
      {
        type: "custom",
        className: "md:col-12 text-center mb-0",
        body: (
          <h3 className="mb-0 uppercase">
            {t('common.statementTitle', {
              action:
                Number(data.documentType) === InvoiceService.documentType_Cancel
                  ? t('common.cancelled')
                  : Number(data.documentType) === InvoiceService.documentType_Substitute
                    ? t('common.replacement')
                    : t('common.adjustment'),
            })}
          </h3>
        ),
      },
      {
        field: "documentNo",
        header: t('common.statementNumber'),
        InputTextProps: { maxLength: 400 },
        className: "md:col-12",
        labelClassName: "md:col-5 justify-content-end",
        inputClassName: "md:col-3",
      },
      {
        field: "reasonAfter",
        header: "",
        type: "InputTextarea",
        className: "md:col-12",
        inputClassName: "font-italic",
      },
      {
        field: "documentDate",
        header: t('common.todayDate'),
        type: "Calendar",
        className: "md:col-4",
        labelClassName: "md:col-5",
        inputClassName: "md:col-7",
      },
      {
        type: "custom",
        className: "md:col-6 pt-2",
        body: <p>{t('common.parties')}</p>,
      },
      {
        field: "sellerLegalName",
        header: t('common.seller'),
        type: "text",
        className: "md:col-12",
        labelClassName: "md:col-2 font-bold",
        inputClassName: "md:col-10 font-bold h-35 lh-35",
      },
      {
        field: "sellerAddress",
        header: t('common.address'),
        type: "text",
        className: "md:col-12",
        labelClassName: "md:col-2",
        inputClassName: "md:col-10 h-35 lh-35",
      },
      {
        field: "sellerTaxCode",
        header: t('common.taxCode'),
        type: "text",
        className: "md:col-6",
        labelClassName: "md:col-4",
        inputClassName: "md:col-8 h-35 lh-35",
      },
      {
        field: "sellerTel",
        header: t('common.phone'),
        className: "md:col-6",
        labelClassName: "md:col-4",
        inputClassName: "md:col-8",
      },
      {
        field: "sellerPersonContact",
        header: t('common.representative'),
        className: "md:col-6",
        labelClassName: "md:col-4",
        inputClassName: "md:col-8",
      },
      {
        field: "sellerPosition",
        header: t('common.position'),
        className: "md:col-6",
        labelClassName: "md:col-4",
        inputClassName: "md:col-8",
      },
      {
        field: "buyerLegalName",
        header: t('common.buyer'),
        className: "md:col-12",
        labelClassName: "md:col-2 font-bold",
        inputClassName: "md:col-10 font-bold",
        required: true,
      },
      {
        field: "buyerAddress",
        header: t('common.address'),
        className: "md:col-12",
        labelClassName: "md:col-2",
        inputClassName: "md:col-10",
        required: true,
      },
      {
        field: "buyerTaxCode",
        header: t('common.taxCode'),
        className: "md:col-6",
        labelClassName: "md:col-4",
        inputClassName: "md:col-8",
      },
      {
        field: "buyerTel",
        header: t('common.phone'),
        className: "md:col-6",
        labelClassName: "md:col-4",
        inputClassName: "md:col-8",
      },
      {
        field: "buyerPersonContact",
        header: t('common.representative'),
        className: "md:col-6",
        labelClassName: "md:col-4",
        inputClassName: "md:col-8",
        required: isRequiredBuyerPersonContact,
      },
      {
        field: "buyerPosition",
        header: t('common.position'),
        className: "md:col-6",
        labelClassName: "md:col-4",
        inputClassName: "md:col-8",
      },
      {
        field: "buyerEmail",
        header: t('auth.email'),
        type: "email",
        className: "md:col-6",
        labelClassName: "md:col-4",
        inputClassName: "md:col-8",
      },
      {
        type: "template",
        className: "md:col-12",
        template: (item) => {
          let {
            invoiceSerial,
            invoiceNumber,
            invoiceDate,
            invoiceListNumber,
            invoiceListDate,
          } = item;
          let form, serial;
          if (invoiceSerial) {
            form = invoiceSerial.substring(0, 1);
            serial = invoiceSerial.substring(1, invoiceSerial.length);
          }
          if (invoiceDate) {
            invoiceDate = FormatDisplay.date(invoiceDate);
          }
          if (invoiceListDate) {
            invoiceListDate = FormatDisplay.date(invoiceListDate);
          }

          if (Number(data.documentType) === InvoiceService.documentType_Cancel) {
            return <i>{t('common.statementCancelSentence', {
              form,
              serial,
              number: invoiceNumber,
              date: invoiceDate,
            })}</i>;
          }

          if (
            Number(data.documentType) === InvoiceService.documentType_Substitute
          ) {
            if (invoiceListNumber || invoiceListDate) {
              return <i>{t('common.statementMultipleSentence', {
                action: t('common.replacement'),
                listNumber: invoiceListNumber || "....",
                listDate: invoiceListDate || "....",
              })}</i>;
            }
            return <i>{t('common.statementSingleSentence', {
              action: t('common.replacement'),
              form,
              serial,
              number: invoiceNumber,
              date: invoiceDate,
            })}</i>;
          }
          if (
            Number(data.documentType) === InvoiceService.documentType_Adjust
          ) {
            if (invoiceListNumber || invoiceListDate) {
              return <i>{t('common.statementMultipleSentence', {
                action: t('common.adjustment'),
                listNumber: invoiceListNumber || "....",
                listDate: invoiceListDate || "....",
              })}</i>;
            }
            return <i>{t('common.statementSingleSentence', {
              action: t('common.adjustment'),
              form,
              serial,
              number: invoiceNumber,
              date: invoiceDate,
            })}</i>;
          }
        },
      },
      {
        field: "reason",
        header: `${t('common.reason')} ${Number(data.documentType) === InvoiceService.documentType_Cancel ? t('common.cancelled') : Number(data.documentType) === InvoiceService.documentType_Substitute ? t('common.replacement') : t('common.adjustment')}`,
        className: "md:col-12",
        labelClassName: "md:col-2",
        inputClassName: "md:col-10",
        required: true,
      },
      {
        type: "custom",
        className: "md:col-12",
        body: (
          <p>{t('common.statementCommitment')}</p>
        ),
      },
      {
        type: "custom",
        className: "md:col-6 mt-3",
        body: (
          <div className="flex flex-column align-items-center gap-2">
            <div className="col-12 text-center">
              <b className="uppercase">{t('common.representativeSeller')}</b>
            </div>
            <div className="col-12 text-center">
              <i>{t('common.digitalSignature')}</i>
            </div>
            {data?.status === 2 && (
              <div className="grid grid-nogutter relative border-2 border-solid border-green-500 text-green-500 px-4 w-9">
                <span className="col-12">{t('common.signatureValid')}</span>
                <span className="col-3">{t('common.signedBy')}</span>
                <span className="col-9">{data?.sellerLegalName}</span>
                <span className="col-3">{t('common.signedDate')}</span>
                <span className="col-9">
                  {moment(data?.dateSign).format("DD/MM/YYYY HH:mm:ss")}
                </span>
                <img
                  className="col-2 absolute"
                  style={{
                    top: "60%",
                    right: "10px",
                    transform: "translateY(-50%)",
                  }}
                  src={"assets/layout/images/check.png"}
                  alt=""
                />
              </div>
            )}
            {/* <div className="col-12 mt-3 text-center un-fluid">
              <Button label="Ký điện tử" icon="pi pi-user-edit" className="p-button-outlined p-button-sm" onClick={doSign}/>
            </div> */}
          </div>
        ),
      },
      {
        type: "custom",
        className: "md:col-6 mt-3",
        body: (
          <div className="flex flex-column align-items-center gap-2">
            <div className="col-12 text-center">
              <b className="uppercase">{t('common.representativeBuyer')}</b>
            </div>
            <div className="col-12 text-center">
              <i>{t('common.digitalSignature')}</i>
            </div>
            {data?.buyerStatus === 2 && (
              <div className="grid grid-nogutter relative border-2 border-solid border-green-500 text-green-500 px-4 w-9">
                <span className="col-12">{t('common.signatureValid')}</span>
                <span className="col-3">{t('common.signedBy')}</span>
                <span className="col-9">{data?.buyerLegalName}</span>
                <span className="col-3">{t('common.signedDate')}</span>
                <span className="col-9">
                  {moment(data?.buyerDateSign).format("DD/MM/YYYY HH:mm:ss")}
                </span>
                <img
                  className="col-2 absolute"
                  style={{
                    top: "60%",
                    right: "10px",
                    transform: "translateY(-50%)",
                  }}
                  src={"assets/layout/images/check.png"}
                  alt=""
                />
              </div>
            )}
          </div>
        ),
      },
    ],
    layout: "Horizontal",
    formClassName: "paper",
  });

  const print = () => {
    window.open(StatementInvoiceService.getPdfUrl(id), "_blank");
  };

  const submit = async (isSign?: boolean) => {
    if (!submitting && formValid()) {
      setSubmitting(true);

      try {
        const dataValue = getValue();
        let method;
        if (dataValue.id === "00000000-0000-0000-0000-000000000000") {
          delete dataValue.id;
          method = StatementInvoiceService.create({ ...dataValue, status: 1 });
        } else {
          method = StatementInvoiceService.update(dataValue.id, dataValue);
        }
        if (data.status === 2) {
          ConfirmDialogService.confirmSignStatement(async () => {
            await method;
            const loadedData = await load();
            if (isSign) {
              sign({
                CommandName: "StatementInvoice",
                Id: loadedData.id,
                InvoiceIdArray: [loadedData.id],
              });
            }
            ToastService.success();
          });
          return;
        }
        await method;
        const loadedData = await load();
        if (isSign) {
          sign({
            CommandName: "StatementInvoice",
            Id: loadedData.id,
            InvoiceIdArray: [loadedData.id],
          });
        }
        ToastService.success();
        return;
        // reloadLazyData();
      } catch ({ response }) {
        ToastService.error(translateError(response));
      } finally {
        setSubmitting(false);
      }
    }
  };

  const signItem = () => {
    const data = getValue();

    sign({
      CommandName: "StatementInvoice",
      Id: data.id,
      InvoiceIdArray: [data.id],
    });
  };

  useEffect(() => {
    if (getValue().buyerTaxCode) {
      return setIsRequiredBuyerPersonContact(true);
    }
    return setIsRequiredBuyerPersonContact(false);
  }, [getValue().buyerTaxCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const documentDateValue = getValue().documentDate;
  const reasonAfterValue = getValue().reasonAfter;

  useEffect(() => {
    if (!documentDateValue) return;

    const lawBaseBefore = getLawBasesText(new Date('2026-06-01'));
    const lawBaseAfter = getLawBasesText(new Date('2026-07-01'));

    const clean = (str: string) => str ? str.replace(/\s+/g, ' ').trim() : '';

    const cleanReason = clean(reasonAfterValue);
    const cleanBefore = clean(lawBaseBefore);
    const cleanAfter = clean(lawBaseAfter);

    const targetReason = getLawBasesText(documentDateValue);

    if (!cleanReason || cleanReason === cleanBefore || cleanReason === cleanAfter) {
      if (cleanReason !== clean(targetReason)) {
        setValue({
          ...getValue(),
          reasonAfter: targetReason
        });
      }
    }
  }, [documentDateValue]); // eslint-disable-line react-hooks/exhaustive-deps


  return (
    <Fragment>
      <Dialog
        header={t('common.statementCreate')}
        visible={display}
        style={{ width: "890px" }}
        position="top"
        onHide={() => setDisplay(false)}
      >
        {renderForm()}

        <div className="flex justify-content-end mt-2">
          <Button
            label={t('common.save')}
            icon="pi pi-check"
            className="p-button-outlined p-button-sm mr-2"
            onClick={() => submit(false)}
            loading={submitting}
          />
          {id && (
            <Button
              label={t('common.sign')}
              icon="pi pi-check"
              className="p-button-outlined p-button-sm mr-2"
              onClick={signItem}
            />
          )}
          <Button
            label={t('common.saveAndSign')}
            icon="pi pi-check"
            className="p-button-outlined p-button-sm mr-2"
            onClick={() => {
              submit(true);
            }}
            loading={submitting}
          />
          {id && (
            <Button
              label={t('common.print')}
              icon="pi pi-print"
              className="p-button-outlined p-button-sm mr-2"
              onClick={print}
            />
          )}
          {data.id !== "00000000-0000-0000-0000-000000000000" && (
            <Button
              label={t('common.sendForSignature')}
              icon="pi pi-envelope"
              className="p-button-outlined p-button-sm mr-2"
              onClick={() => setEmailDialogVisible(true)}
            />
          )}
        </div>
      </Dialog>
      <DialogSendEmail
        display={emailDialogVisible}
        setDisplay={setEmailDialogVisible}
        selectedItems={data}
        sendType={8}
      />
      {renderSign()}
    </Fragment>
  );
};
